<template>
  <div class="kids-detail-page">
    <MainNavbar />
    <KidsStoreNav />

    <nav class="kids-detail__breadcrumb" aria-label="Breadcrumb">
      <router-link :to="{ name: storeRoute }" class="kids-detail__crumb-link">
        <i class="fa-solid fa-child-reaching" aria-hidden="true" />
        Kids store
      </router-link>
      <span class="kids-detail__crumb-sep" aria-hidden="true">/</span>
      <span class="kids-detail__crumb-current">{{ product?.name || "Product" }}</span>
    </nav>

    <div v-if="loading" class="kids-detail__status" aria-live="polite">
      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
      <span>Loading product…</span>
    </div>

    <p v-else-if="loadError" class="kids-detail__status kids-detail__status--error" role="alert">
      {{ loadError }}
      <router-link :to="{ name: storeRoute }" class="kids-detail__back">Back to store</router-link>
    </p>

    <article v-else-if="product" class="kids-detail">
      <div class="kids-detail__layout">
        <div class="kids-detail__gallery">
          <div class="kids-detail__main-image">
            <img
              :src="activeImage"
              :alt="product.name"
              decoding="async"
              @error="onImageError"
            />
            <span v-if="product.discounted" class="kids-detail__sale-tag">Sale</span>
          </div>
          <div v-if="gallery.length > 1" class="kids-detail__thumbs">
            <button
              v-for="(img, idx) in gallery"
              :key="idx"
              type="button"
              class="kids-detail__thumb"
              :class="{ active: activeImage === img }"
              @click="activeImage = img"
            >
              <img :src="img" :alt="`${product.name} view ${idx + 1}`" loading="lazy" />
            </button>
          </div>
        </div>

        <div class="kids-detail__panel">
          <p class="kids-detail__kicker">
            {{ product.stage }}
            <template v-if="product.sport"> · {{ product.sport }}</template>
            · {{ product.sub_category_label }}
          </p>
          <h1 class="kids-detail__title">{{ product.name }}</h1>
          <p v-if="product.brand" class="kids-detail__brand">{{ product.brand }}</p>

          <div class="kids-detail__badges">
            <span class="kids-detail__badge kids-detail__badge--stage">{{ stageShort }}</span>
            <span
              class="kids-detail__badge"
              :class="product.is_in_stock ? 'kids-detail__badge--stock' : ''"
            >
              {{ product.is_in_stock ? "In stock" : "Out of stock" }}
            </span>
            <span v-if="product.size" class="kids-detail__badge">Size {{ product.size }}</span>
            <span v-if="product.returnable" class="kids-detail__badge">Returnable</span>
          </div>

          <div class="kids-detail__price-block">
            <template v-if="product.discounted">
              <span class="kids-detail__price kids-detail__price--was">
                KES {{ formatPrice(product.price) }}
              </span>
              <span class="kids-detail__price kids-detail__price--now">
                KES {{ formatPrice(product.final_price) }}
              </span>
              <span v-if="product.discount_percent" class="kids-detail__save">
                Save {{ product.discount_percent }}%
              </span>
            </template>
            <span v-else class="kids-detail__price kids-detail__price--now">
              KES {{ formatPrice(product.price) }}
            </span>
          </div>

          <p class="kids-detail__sku">SKU: {{ product.sku }}</p>
          <p class="kids-detail__short">{{ product.short_description }}</p>

          <div v-if="product.is_in_stock" class="kids-detail__qty">
            <span class="kids-detail__qty-label">Quantity</span>
            <div class="kids-detail__qty-control">
              <button type="button" :disabled="quantity <= 1" @click="quantity -= 1">−</button>
              <span>{{ quantity }}</span>
              <button
                type="button"
                :disabled="quantity >= product.stock_quantity"
                @click="quantity += 1"
              >
                +
              </button>
            </div>
          </div>

          <div class="kids-detail__actions">
            <button
              type="button"
              class="kids-detail__btn kids-detail__btn--primary"
              :disabled="!product.is_in_stock || addingToCart"
              @click="addToCart"
            >
              <i class="fa-solid fa-cart-shopping" aria-hidden="true" />
              {{ addingToCart ? "Adding…" : "Add to cart" }}
            </button>
            <button
              type="button"
              class="kids-detail__btn kids-detail__btn--ghost"
              disabled
              title="Wishlist coming soon"
            >
              <i class="fa-solid fa-heart" aria-hidden="true" />
              Wishlist
            </button>
          </div>

          <section v-if="product.description" class="kids-detail__section">
            <h2>About this item</h2>
            <p class="kids-detail__desc">{{ product.description }}</p>
          </section>

          <section class="kids-detail__section kids-detail__section--muted">
            <h2>Reviews</h2>
            <p class="kids-detail__reviews-placeholder">
              <i class="fa-solid fa-star" aria-hidden="true" />
              Reviews are coming soon for the kids store.
            </p>
          </section>
        </div>
      </div>

      <section v-if="related.length" class="kids-detail__related">
        <div class="kids-detail__related-head">
          <h2>Related products</h2>
          <p>More gear from the same stage and category.</p>
        </div>
        <div class="kids-detail__related-grid">
          <article
            v-for="item in related"
            :key="item.item_id"
            class="kids-detail__related-card"
            @click="goToProduct(item.slug)"
          >
            <img :src="item.thumbnail" :alt="item.name" loading="lazy" />
            <div class="kids-detail__related-body">
              <p class="kids-detail__related-cat">{{ item.sub_category_label }}</p>
              <h3>{{ item.name }}</h3>
              <p class="kids-detail__related-price">KES {{ formatPrice(item.final_price) }}</p>
            </div>
          </article>
        </div>
      </section>
    </article>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import KidsStoreNav from "./components/KidsStoreNav.vue";
import { buildApiUrl } from "@/config/api";
import { useKidsCartStore } from "@/cartstore/kidsCartStore";
import globalToastMixin from "@/mixins/globalToastMixin";
import { KIDS_API, KIDS_DETAIL_ROUTE, KIDS_STORE_ROUTE } from "./constants";
import { kidsProductToCartLine } from "./kidsCart";

const PLACEHOLDER = buildApiUrl("/static/images/product_images/placeholder.png");

export default {
  name: "KidsProductDetail",
  components: { MainNavbar, MainFooter, KidsStoreNav },
  mixins: [globalToastMixin],
  setup() {
    const kidsCartStore = useKidsCartStore();
    return { kidsCartStore };
  },
  data() {
    return {
      product: null,
      related: [],
      loading: true,
      loadError: "",
      activeImage: PLACEHOLDER,
      quantity: 1,
      addingToCart: false,
      storeRoute: KIDS_STORE_ROUTE,
    };
  },
  computed: {
    gallery() {
      if (!this.product) return [PLACEHOLDER];
      const imgs = [this.product.thumbnail, ...(this.product.images || [])].filter(Boolean);
      return imgs.length ? imgs : [PLACEHOLDER];
    },
    stageShort() {
      const map = { 1: "Early", 2: "Structured", 3: "Advanced" };
      return map[this.product?.development_stage] || this.product?.stage || "Kids";
    },
  },
  watch: {
    "$route.params.slug": {
      immediate: true,
      handler() {
        this.fetchProduct();
      },
    },
  },
  methods: {
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-KE");
    },
    onImageError() {
      this.activeImage = PLACEHOLDER;
    },
    goToProduct(slug) {
      if (!slug || slug === this.product?.slug) return;
      this.$router.push({ name: KIDS_DETAIL_ROUTE, params: { slug } });
    },
    async fetchProduct() {
      const slug = this.$route.params.slug;
      if (!slug) {
        this.loadError = "Invalid product link.";
        this.loading = false;
        return;
      }

      this.loading = true;
      this.loadError = "";
      this.quantity = 1;
      try {
        const [detailRes, relatedRes] = await Promise.all([
          fetch(buildApiUrl(KIDS_API.detail(slug))),
          fetch(buildApiUrl(KIDS_API.related(slug))),
        ]);
        const detail = await detailRes.json().catch(() => ({}));
        if (!detailRes.ok) {
          throw new Error(detail.detail || "Product not found.");
        }
        this.product = detail;
        this.activeImage = this.gallery[0];

        const relatedData = await relatedRes.json().catch(() => ({}));
        this.related = relatedRes.ok && Array.isArray(relatedData.items) ? relatedData.items : [];
      } catch (err) {
        this.product = null;
        this.related = [];
        this.loadError = err.message || "Could not load product.";
      } finally {
        this.loading = false;
      }
    },
    addToCart() {
      if (!this.product?.is_in_stock) return;
      this.addingToCart = true;
      try {
        const line = kidsProductToCartLine(this.product);
        this.kidsCartStore.addToCart(line, this.quantity);
        this.showToast(
          "Added to cart",
          `${this.product.name} (${this.quantity}) added to your kids cart.`,
          "success",
        );
      } catch (err) {
        this.showToast("Sign in required", err.message || "Please log in to add items.", "error");
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
</script>

<style scoped>
.kids-detail-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--background-color2);
  color: var(--text-color1);
  padding-top: max(68px, calc(env(safe-area-inset-top, 0px) + 60px));
}

.kids-detail__breadcrumb {
  width: 100%;
  padding: 0.75rem clamp(0.875rem, 3.5vw, 2.75rem) 0;
  font-size: 0.85rem;
  color: var(--text-color2);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  box-sizing: border-box;
}

.kids-detail__crumb-link {
  color: var(--border-color-hover);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.kids-detail__crumb-link:hover {
  text-decoration: underline;
}

.kids-detail__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 3rem 1rem;
  color: var(--text-color2);
}

.kids-detail__status--error {
  flex-direction: column;
  color: #c62828;
  text-align: center;
}

.kids-detail__back {
  margin-top: 0.5rem;
  color: var(--border-color-hover);
}

.kids-detail {
  width: 100%;
  padding: 0 clamp(0.875rem, 3.5vw, 2.75rem) clamp(2rem, 5vw, 3rem);
  box-sizing: border-box;
}

.kids-detail__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: clamp(1.25rem, 3vw, 2.5rem);
  align-items: start;
}

.kids-detail__gallery {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 16px;
  padding: clamp(0.75rem, 2vw, 1rem);
}

.kids-detail__main-image {
  position: relative;
  min-height: clamp(220px, 40vw, 420px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color2);
  border-radius: 12px;
  overflow: hidden;
}

.kids-detail__main-image img {
  width: 100%;
  max-height: 480px;
  object-fit: contain;
  padding: 1rem;
}

.kids-detail__sale-tag {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: #e53935;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
}

.kids-detail__thumbs {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.kids-detail__thumb {
  flex: 0 0 72px;
  height: 72px;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: var(--background-color2);
}

.kids-detail__thumb.active {
  border-color: var(--border-color-hover);
}

.kids-detail__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.kids-detail__panel {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.5rem);
}

.kids-detail__kicker {
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color2);
}

.kids-detail__title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.35rem, 3.5vw, 2rem);
  line-height: 1.15;
  font-weight: 800;
}

.kids-detail__brand {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.kids-detail__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.kids-detail__badge {
  font-size: 0.72rem;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  color: var(--text-color2);
}

.kids-detail__badge--stage {
  background: color-mix(in srgb, var(--border-color-hover) 15%, var(--background-color1));
  color: var(--border-color-hover);
  border-color: transparent;
}

.kids-detail__badge--stock {
  color: #2e7d32;
  border-color: rgba(46, 125, 50, 0.35);
}

.kids-detail__price-block {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
}

.kids-detail__price--now {
  font-size: clamp(1.2rem, 3vw, 1.6rem);
  font-weight: 800;
  color: var(--border-color-hover);
}

.kids-detail__price--was {
  font-size: 0.95rem;
  text-decoration: line-through;
  color: var(--text-color2);
}

.kids-detail__save {
  font-size: 0.78rem;
  font-weight: 700;
  color: #2e7d32;
}

.kids-detail__sku {
  margin: 0 0 0.75rem;
  font-size: 0.78rem;
  color: var(--text-color2);
}

.kids-detail__short {
  margin: 0 0 1rem;
  line-height: 1.55;
  color: var(--text-color2);
}

.kids-detail__qty {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.kids-detail__qty-label {
  font-size: 0.85rem;
  font-weight: 600;
}

.kids-detail__qty-control {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--background-color3);
  border-radius: 10px;
  overflow: hidden;
}

.kids-detail__qty-control button {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--background-color2);
  cursor: pointer;
  font-size: 1.1rem;
}

.kids-detail__qty-control button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.kids-detail__qty-control span {
  min-width: 2.5rem;
  text-align: center;
  font-weight: 700;
}

.kids-detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 1.25rem;
}

.kids-detail__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 44px;
  padding: 0.65rem 1.25rem;
  border-radius: 12px;
  font: inherit;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: transform 0.2s, opacity 0.2s;
}

.kids-detail__btn--primary {
  flex: 1 1 180px;
  background: var(--border-color-hover);
  color: #fff;
}

.kids-detail__btn--primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.kids-detail__btn--ghost {
  flex: 0 1 auto;
  background: transparent;
  border: 1px solid var(--background-color3);
  color: var(--text-color2);
  cursor: not-allowed;
  opacity: 0.7;
}

.kids-detail__btn--primary:not(:disabled):hover {
  transform: translateY(-2px);
}

.kids-detail__section {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--background-color3);
}

.kids-detail__section h2 {
  margin: 0 0 0.65rem;
  font-size: 1rem;
}

.kids-detail__desc {
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
  color: var(--text-color2);
}

.kids-detail__section--muted {
  opacity: 0.85;
}

.kids-detail__reviews-placeholder {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color2);
  font-size: 0.88rem;
}

.kids-detail__related {
  margin-top: clamp(2rem, 4vw, 3rem);
  padding-top: clamp(1.25rem, 3vw, 2rem);
  border-top: 1px solid var(--background-color3);
}

.kids-detail__related-head h2 {
  margin: 0 0 0.25rem;
  font-size: clamp(1.1rem, 2.5vw, 1.35rem);
}

.kids-detail__related-head p {
  margin: 0 0 1rem;
  color: var(--text-color2);
  font-size: 0.88rem;
}

.kids-detail__related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
  gap: clamp(0.75rem, 2vw, 1.25rem);
}

.kids-detail__related-card {
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.kids-detail__related-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--text-color1) 8%, transparent);
}

.kids-detail__related-card img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.kids-detail__related-body {
  padding: 0.75rem;
}

.kids-detail__related-cat {
  margin: 0 0 0.2rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--border-color-hover);
}

.kids-detail__related-body h3 {
  margin: 0 0 0.35rem;
  font-size: 0.88rem;
  line-height: 1.3;
}

.kids-detail__related-price {
  margin: 0;
  font-weight: 800;
  font-size: 0.85rem;
}

@media (max-width: 900px) {
  .kids-detail__layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .kids-detail__actions {
    flex-direction: column;
  }

  .kids-detail__btn {
    width: 100%;
  }
}
</style>
